// =============================================================================
// staticSite/wpSiteDetect — automatyczne wykrycie ustawien migracji zamiast
// wpisywania recznego:
//   - nazwa strony + tagline        <- WP REST root  GET /wp-json/
//   - baza adresow kategorii        <- GET /wp-json/wp/v2/categories (link pierwszej)
//   - hosty linkow wychodzacych     <- final_html opublikowanych artykulow + wp_posts
// Endpointy root/categories sa publiczne (bez auth). Kazde zrodlo ma osobny
// try/catch — brak jednego nie blokuje reszty (warnings).
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { domainFromUrl, hostOf, isSiteHost } from "./urlPolicy";

export interface DetectedHost {
  host: string;
  count: number;
  /** Ile roznych stron linkuje do hosta. */
  pages: number;
}

export interface DetectedWpSettings {
  siteName: string | null;
  tagline: string | null;
  categoryBase: string | null;
  hosts: DetectedHost[];
  warnings: string[];
}

const TIMEOUT_MS = 12_000;

async function fetchJson<T>(url: string): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

/** "https://x.pl/kategoria/rowery/" -> "kategoria"; "https://x.pl/rowery/" -> "" */
export function categoryBaseFromLink(link: string, slug: string): string | null {
  try {
    const path = new URL(link).pathname.replace(/^\/+|\/+$/g, "");
    const parts = path.split("/").filter(Boolean);
    if (!parts.length) return null;
    const idx = parts.lastIndexOf(slug);
    if (idx < 0) return null;
    return parts.slice(0, idx).join("/");
  } catch {
    return null;
  }
}

const HREF_RE = /<a\b[^>]*href\s*=\s*"(https?:\/\/[^"]+)"/gi;

export function countExternalHosts(htmls: string[], domain: string): DetectedHost[] {
  const counts = new Map<string, { count: number; pages: Set<number> }>();
  htmls.forEach((html, i) => {
    for (const m of (html || "").matchAll(HREF_RE)) {
      const host = hostOf(m[1]).replace(/^www\./, "");
      if (!host || isSiteHost(host, domain)) continue;
      const cur = counts.get(host) || { count: 0, pages: new Set<number>() };
      cur.count++;
      cur.pages.add(i);
      counts.set(host, cur);
    }
  });
  return [...counts.entries()]
    .map(([host, v]) => ({ host, count: v.count, pages: v.pages.size }))
    .sort((a, b) => b.pages - a.pages || b.count - a.count);
}

export async function detectWpSettings(mapId: string, websiteId: string): Promise<DetectedWpSettings> {
  const warnings: string[] = [];
  const out: DetectedWpSettings = { siteName: null, tagline: null, categoryBase: null, hosts: [], warnings };

  const { data: website } = await (supabase as any).from("websites").select("url, name").eq("id", websiteId).maybeSingle();
  if (!website?.url) {
    warnings.push("Domena nie ma adresu URL.");
    return out;
  }
  const base = String(website.url).replace(/\/$/, "");
  const domain = domainFromUrl(base);

  // 1. Root REST: nazwa + tagline
  try {
    const root = await fetchJson<{ name?: string; description?: string }>(`${base}/wp-json/`);
    out.siteName = root.name?.trim() || null;
    out.tagline = root.description?.trim() || null;
  } catch (e) {
    warnings.push(`WP REST root niedostępny (${e instanceof Error ? e.message : e}) — tagline do wpisania ręcznie.`);
  }

  // 2. Kategorie: baza permalinkow
  try {
    const cats = await fetchJson<Array<{ slug: string; link: string; count?: number }>>(`${base}/wp-json/wp/v2/categories?per_page=100&orderby=count&order=desc`);
    const withPosts = cats.filter((c) => (c.count ?? 1) > 0 && c.slug !== "bez-kategorii" && c.slug !== "uncategorized");
    const pick = withPosts[0] || cats[0];
    if (pick) {
      const cb = categoryBaseFromLink(pick.link, pick.slug);
      if (cb !== null) out.categoryBase = cb;
    }
    if (out.categoryBase === null) warnings.push("Nie udało się odczytać struktury adresów kategorii — zostaje domyślne /category/.");
  } catch (e) {
    warnings.push(`Kategorie WP niedostępne (${e instanceof Error ? e.message : e}).`);
  }

  // 3. Hosty linkow wychodzacych z tresci (DB — bez sieci)
  try {
    const [{ data: contents }, { data: posts }] = await Promise.all([
      (supabase as any).from("thematic_node_content").select("final_html").eq("map_id", mapId).eq("engine_stage", "published"),
      (supabase as any).from("wp_posts").select("content").eq("website_id", websiteId).eq("status", "publish"),
    ]);
    const htmls = [
      ...((contents || []) as Array<{ final_html: string | null }>).map((c) => c.final_html || ""),
      ...((posts || []) as Array<{ content: string | null }>).map((p) => p.content || ""),
    ];
    out.hosts = countExternalHosts(htmls, domain);
    if (!htmls.length) warnings.push("Brak opublikowanych treści — lista hostów pusta.");
  } catch (e) {
    warnings.push(`Nie udało się przeskanować treści (${e instanceof Error ? e.message : e}).`);
  }

  return out;
}
